import React, { useRef, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';

interface CityAutocompleteProps {
  value: string;
  onChange: (city: string) => void;
}

declare global {
  interface Window {
    google?: any;
  }
}

export default function CityAutocomplete({ value, onChange }: CityAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const initAutocomplete = useCallback(() => {
    if (!inputRef.current || !window.google?.maps?.places || autocompleteRef.current) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['(cities)'],
    });

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (place?.name) {
        onChangeRef.current(place.name);
      }
    });
  }, []);

  useEffect(() => {
    // Try immediately
    initAutocomplete();

    // If Google isn't loaded yet, poll until it is
    if (!window.google?.maps?.places) {
      const interval = setInterval(() => {
        if (window.google?.maps?.places) {
          initAutocomplete();
          clearInterval(interval);
        }
      }, 200);
      return () => clearInterval(interval);
    }

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [initAutocomplete]);

  return (
    <Input
      ref={inputRef}
      defaultValue={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Start typing your city..."
    />
  );
}
